import React, { createContext, useReducer, useRef } from "react";
import io from "socket.io-client";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { toastSuccess, toastError, toastIcon } from "../utils/Toast";
import { te } from "../utils/errorsTranslation";

import SocketReducer, {
  INIT_SOCKET,
  GRAPH_UPDATE_INTERVAL,
  SET_GRAPH,
  DISCONNECT,
  CHAT_ECHO,
  CHAT_UPDATE,
  CHAT_RESUME,
  NOTE_ECHO,
  NOTE_UPDATE,
  NOTE_RESUME,
  FUNCTIONALITIES_ECHO,
  SLIDE_ECHO,
  PARTICIPANTS_RESUME,
  SYNC_UNDERSTANDING_ECHO,
  ASYNC_UNDERSTANDING_ECHO,
  ASYNC_UNDERSTANDING_RESUME,
  SYNC_UNDERSTANDING_RESUME,
  GRAPH_UPDATE,
  STOP_GRAPH_UPDATE,
  BOOKMARKS_RESUME,
  BOOKMARK_ECHO,
} from "./SocketReducer";

//Socket server URL
const socketUrl =
  process.env.NODE_ENV === "production" ? process.env.REACT_APP_SOCKET_PROD_URL : process.env.REACT_APP_SOCKET_DEV_URL;
//Context initial state
const initialState = {
  //Client socket reference
  socket: null,
  connectedRoom: null,
  chatMessages: {},
  questions: [],
  notes: [],
  participants: [],
  functionalities: { chat: false, notes: false, feedback: false },
  syncUnderstandings: [],
  asyncUnderstandings: [],
  globalUnderstanding: { counters: { low: 0, mid: 0, high: 0 }, videoTs: 0 },
  techerSlideNumber: 1,
  bookmarks: [],
  graphCache: [],
  graphInterval: null,
  graphStartTime: 0,
  graphVideoTime: 0,
};

export const MINUTES_TO_DISPLAY = 5;

export const SocketContext = createContext(initialState);

export const SocketProvider = ({ children }) => {
  const [state, dispatch] = useReducer(SocketReducer, initialState);
  const connectionAttempts = useRef(0);
  const history = useHistory();
  const { t } = useTranslation("", { keyPrefix: "socket" });

  //Connect the socket to a socket server
  function connectSocket(teacher = false, lectureUUID) {
    const socket = io(socketUrl, {
      reconnectionAttempts: 3,
      auth: {
        token: Cookies.get("access-token"),
      },
    });

    socket.once("connect", () => onConnect(teacher, lectureUUID, socket));
    socket.on("connect_error", onConnectError);

    dispatch({ type: INIT_SOCKET, payload: { socket, room: lectureUUID } });
  }

  //Remove events listeners and disconnect socket server
  function disconnectSocket() {
    dispatch({ type: DISCONNECT });
  }

  function sendSocketEvent(eventName, body) {
    state.socket.emit(eventName, body);
    //Get current video time
  }

  //Socket connection handler
  function onConnect(teacher, lectureUUID, socket) {
    if (connectionAttempts.current > 0) {
      toastSuccess(t("reconnection"));
      connectionAttempts.current = 0;
    }

    const event = teacher ? "connection/teacherJoin" : "connection/studentJoin";
    socket.emit(event, { room: lectureUUID });

    //Bind all listeners
    bindListeners(socket);
  }

  function onConnectError() {
    //For three time try to reconnect
    if (connectionAttempts.current < 2) {
      connectionAttempts.current++;
      toastError(t("connection_error", { attempt: connectionAttempts.current }));
    } else {
      connectionAttempts.current = 0;
      //Redirect to home page
      toastError(t("connection_fail", { attempt: connectionAttempts.current }));
      history.push("/"); //redirect
    }
  }

  function setGraph(graph) {
    dispatch({ type: SET_GRAPH, payload: graph });
  }

  function updateGraph() {
    dispatch({ type: GRAPH_UPDATE });
  }

  function setupGraphUpdate() {
    setGraph(new Array(60 * MINUTES_TO_DISPLAY).fill(undefined));

    dispatch({ type: GRAPH_UPDATE_INTERVAL, payload: setInterval(updateGraph, 1000) });
  }

  function stopGraphUpdate() {
    dispatch({ type: STOP_GRAPH_UPDATE });
  }

  function bindListeners(socket) {
    socket.on("chat/chatEcho", (message) => {
      dispatch({
        type: CHAT_ECHO,
        payload: message,
      });
    });

    socket.on("chat/chatUpdate", (message) => {
      dispatch({
        type: CHAT_UPDATE,
        payload: message,
      });
    });

    socket.on("chat/chatResume", (messages) => {
      dispatch({
        type: CHAT_RESUME,
        payload: messages,
      });
    });

    socket.on("notes/noteEcho", (note) => {
      dispatch({
        type: NOTE_ECHO,
        payload: note,
      });
    });

    socket.on("notes/noteUpdate", ({ note }) => {
      dispatch({
        type: NOTE_UPDATE,
        payload: note,
      });
    });

    socket.on("notes/noteResume", (notes) => {
      dispatch({
        type: NOTE_RESUME,
        payload: notes,
      });
    });

    socket.on("teacher/functionalitiesEcho", ({ chat, notes, feedback }) => {
      dispatch({
        type: FUNCTIONALITIES_ECHO,
        payload: { chat, notes, feedback },
      });
    });

    socket.on("teacher/slideEcho", ({ pageNumber }) => {
      dispatch({
        type: SLIDE_ECHO,
        payload: pageNumber,
      });
    });

    socket.on("teacher/banEcho", () => {
      toastIcon("⛔", "You have been banned from the lecture");
      history.push("/");
    });

    socket.on("teacher/endLectureEcho", (lecture) => {
      toastIcon("🔔", "Teacher closed the lecture!");
      history.push("/");
    });

    socket.on("info/participantsResume", ({ students, teacher }) => {
      dispatch({
        type: PARTICIPANTS_RESUME,
        payload: { students, teacher },
      });
    });

    socket.on("feedback/syncUnderstandingEcho", ({ counters, videoTs }) => {
      dispatch({
        type: SYNC_UNDERSTANDING_ECHO,
        payload: { counters, videoTs },
      });
    });

    socket.on("feedback/asyncUnderstandingEcho", (reaction) => {
      dispatch({
        type: ASYNC_UNDERSTANDING_ECHO,
        payload: reaction,
      });
    });

    socket.on("feedback/asyncUnderstandingResume", (history) => {
      dispatch({
        type: ASYNC_UNDERSTANDING_RESUME,
        payload: history,
      });
    });

    socket.on("feedback/syncUnderstandingResume", (history) => {
      dispatch({
        type: SYNC_UNDERSTANDING_RESUME,
        payload: history,
      });
    });

    socket.on("bookmarks/bookmarkEcho", (bookmark) => {
      dispatch({ type: BOOKMARK_ECHO, payload: bookmark });
    });

    socket.on("bookmarks/bookmarksResume", (bookmarks) => {
      dispatch({ type: BOOKMARKS_RESUME, payload: bookmarks });
    });

    socket.on("error", () => {
      toastError(te("socket_error"));
      history.push("/"); //redirect
    });
  }

  return (
    <SocketContext.Provider
      value={{
        socket: state.socket,
        connectedRoom: state.connectedRoom,
        chatMessages: state.chatMessages,
        questions: state.questions,
        notes: state.notes,
        participants: state.participants,
        functionalities: state.functionalities,
        syncUnderstandings: state.syncUnderstandings,
        asyncUnderstandings: state.asyncUnderstandings,
        globalUnderstanding: state.globalUnderstanding,
        teacherSlideNumber: state.teacherSlideNumber,
        bookmarks: state.bookmarks,
        graphCache: state.graphCache,
        graphVideoTime: state.graphVideoTime,
        connectSocket,
        disconnectSocket,
        sendSocketEvent,
        updateGraph,
        setGraph,
        setupGraphUpdate,
        stopGraphUpdate,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};
